import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FolderOpen } from "lucide-react";
import { API } from "@/lib/api";
import { PORTFOLIO } from "@/data/portfolio";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/SectionHead";

export function PortfolioPreview() {
  const [items, setItems] = useState(PORTFOLIO || null);
  useEffect(() => {
    API.get("/portfolio").then((r) => {
      if (r?.data?.items?.length) setItems(r.data.items);
    }).catch(() => {});
  }, []);

  return (
    <section className="py-16 sm:py-24 lg:py-32" data-testid="portfolio-preview-section">
      <div className="container-rw">
        <SectionHead
          eyebrow="Proof of Work"
          title="SEO work that can be measured."
          description="Real projects with verified before/after metrics. We publish only what we can stand behind — no fabricated numbers, ever."
          testid="portfolio-preview-head"
        />
        <Reveal className="mt-12">
          {items && items.length > 0 ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {items.slice(0, 3).map((p) => (
                <Link key={p.slug} to="/portfolio" data-testid={`portfolio-preview-${p.slug}`}
                  className="group overflow-hidden rounded-2xl border border-rw-border bg-rw-card transition-all hover:border-rw-green/50 hover:-translate-y-1">
                  {p.image && (
                    <div className="overflow-hidden border-b border-rw-border">
                      <img src={p.image} alt={`${p.client} website`} loading="lazy" width="640" height="400"
                        className="aspect-[16/10] w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                    </div>
                  )}
                  <div className="p-6">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-rw-blue">{p.industry}</p>
                    <h3 className="mt-2 font-jakarta text-lg font-bold text-rw-text group-hover:text-rw-green transition-colors">{p.client}</h3>
                    {p.scope && <p className="mt-1.5 font-mono text-[10px] uppercase tracking-wider text-rw-green">{p.scope}</p>}
                    <p className="mt-2 text-sm text-rw-muted line-clamp-2">{p.strategy || p.problem}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-rw-border2 bg-rw-card/50 p-10 sm:p-14 text-center" data-testid="portfolio-empty-state">
              <FolderOpen className="mx-auto text-rw-green" size={36} />
              <h3 className="mt-5 font-jakarta text-xl sm:text-2xl font-bold text-rw-text">
                Your business could be our next case study.
              </h3>
              <p className="mx-auto mt-3 max-w-xl text-sm sm:text-base text-rw-muted">
                We publish only verified client work — real audits, real metrics, real outcomes. Case studies from
                current engagements are being documented and will appear here once client approval is in place.
              </p>
              <Link to="/contact" data-testid="portfolio-empty-cta"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-rw-green px-7 py-3 text-sm font-semibold text-rw-bg transition-all hover:bg-rw-green2 hover:-translate-y-0.5">
                Become the First Case Study <ArrowUpRight size={15} />
              </Link>
            </div>
          )}
        </Reveal>
      </div>
    </section>
  );
}
